/**
 * OntologyEnabledBoundedContext - Full-featured BoundedContext with ontology and KG management.
 *
 * This class extends the core BoundedContext functionality with:
 * - IRI → OntologySchema mapping for quick lookup
 * - Eager loading of imported ontologies when an ontology is added
 * - Auto-loading of domains marked with autoloaded=true in domain.json
 * - Cycle detection for import resolution
 * - KnowledgeGraph management
 * - Taxonomy building from loaded ontologies
 *
 * For the core interface without these dependencies, use BoundedContextImpl
 * from semantical-core module.
 *
 * @see BoundedContextImpl from semantical-core for the minimal implementation
 */

import type OntologySchema from '@semantical/modules/ontology-model/OntologySchema.ts';
import type { BoundedContext, Domain, Model, UbiquitousLanguage, VocabularyRef, View } from '@semantical/modules/semantical-core';
import { ModelImpl, UbiquitousLanguageImpl } from '@semantical/modules/semantical-core';
import {
    getDomainByNamespace,
    type DomainConfig
} from './domainRegistry.ts';
import {
    buildClassTaxonomyFromOntology,
    buildClassTaxonomyFromMultipleOntologies,
    buildRelationshipTaxonomyFromOntology,
    buildClassHierarchyFromOntology
} from '@semantical/modules/ontology-model/taxonomyBuilders.ts';
import type {
    ClassTaxonomyNode,
    RelationshipTaxonomyNode,
    ClassHierarchyMap
} from '@semantical/modules/ontology-model/types.ts';
import { KnowledgeGraph } from '@semantical/modules/knowledge-graph';
import { getAnnotationValueWithLanguagePreference } from '@semantical/modules/owl2/queries.ts';
import type { ProgressCallback } from '@semantical/services/ontology';

/**
 * Options for configuring OntologyEnabledBoundedContext behavior.
 */
export interface OntologyEnabledBoundedContextOptions {
    /**
     * If true, throw an error when an imported ontology cannot be resolved.
     * If false (default), log a warning and continue.
     */
    strictImports?: boolean;

    /**
     * Custom loader for resolving ontology IRIs to OntologySchema instances.
     * Called when an import cannot be resolved from the domain registry.
     */
    ontologyLoader?: (iri: string) => Promise<OntologySchema | undefined>;
}

/**
 * OntologyEnabledBoundedContext manages a collection of loaded ontologies.
 *
 * OntologyEnabledBoundedContext IS a BoundedContext - it implements the interface directly,
 * making them the same entity. Every OntologyEnabledBoundedContext belongs to a Domain
 * which must be provided during construction.
 *
 * @example
 * ```typescript
 * // Create bounded context and add ontologies
 * const boundedContext = new OntologyEnabledBoundedContext(...);
 * await boundedContext.addOntology(myOntologySchema);
 *
 * // Look up by IRI
 * const schema = boundedContext.getOntologyByIRI('http://example.org/my-ontology#');
 * ```
 */
export class OntologyEnabledBoundedContext implements BoundedContext {
    // ==================== BoundedContext Implementation ====================

    /**
     * Surrogate identifier for the bounded context.
     * For convenience, but the composite key (domainId, localId) is authoritative.
     */
    readonly id: string;

    /**
     * Local identifier within the domain.
     * Combined with domainId forms the composite logical identifier.
     */
    readonly localId: string;

    /**
     * Domain identifier - rigid/immutable, established at creation.
     * Part of the composite logical identifier (domainId, localId).
     * This is existential: the bounded context cannot exist without this domain.
     */
    readonly domainId: string;

    /** URI identifier for the bounded context */
    readonly uri: string;

    /** Display name for the bounded context */
    readonly name: string;

    /** Natural language definition of this context's purpose */
    readonly definition?: string;

    /**
     * The domain this bounded context belongs to.
     * This relationship is rigid - once established, it cannot change.
     */
    private readonly _domain: Domain;

    /** The contextual model for this bounded context */
    private readonly _model: ModelImpl;

    /** The Ubiquitous Language for this bounded context */
    private readonly _ubiquitousLanguage: UbiquitousLanguageImpl;

    // ==================== Ontology Management ====================

    /** Map from ontology IRI to OntologySchema */
    private ontologies: Map<string, OntologySchema> = new Map();

    /** Map from source URL to ontology IRI (for duplicate detection with different URLs) */
    private sourceUrlToIri: Map<string, string> = new Map();

    /** Set of IRIs currently being loaded (for cycle detection) */
    private loadingIris: Set<string> = new Set();

    /** Configuration options */
    private options: OntologyEnabledBoundedContextOptions;

    /** Factory function for creating OntologySchema from DomainConfig */
    private schemaFactory?: (domain: DomainConfig) => Promise<OntologySchema>;

    /** Current progress callback (set during loadFromUrl) */
    private currentProgressCallback?: ProgressCallback;

    /**
     * KnowledgeGraph for storing inferred relationships.
     * This is separate from the user's asserted KnowledgeGraph.
     * Populated by the InferenceEngine during inference runs.
     */
    private inferredKnowledgeGraph: KnowledgeGraph | null = null;

    // ==================== View Management ====================

    /**
     * Ordered array of views owned by this bounded context.
     * The array order is authoritative - views are displayed in this order.
     */
    private _views: View[] = [];

    /** Map from dataset ID to its associated KnowledgeGraph (ABox) */
    private knowledgeGraphs: Map<string, KnowledgeGraph> = new Map();

    /**
     * Ordered array of dataset references owned by this bounded context.
     * The array order is authoritative - datasets are displayed in this order.
     */
    private _datasets: import("@semantical/modules/semantical-core").DatasetRef[] = [];

    /** Counter for generating unique bounded context IDs */
    private static ontologyEnabledContextCounter = 0;

    /**
     * Create a new OntologyEnabledBoundedContext instance.
     *
     * @param options - Configuration options for the bounded context
     * @param schemaFactory - Optional factory function for creating OntologySchema from DomainConfig
     * @param contextConfig - Optional configuration for the bounded context identity
     */
    constructor(
        options: OntologyEnabledBoundedContextOptions = {},
        schemaFactory?: (domain: DomainConfig) => Promise<OntologySchema>,
        contextConfig?: {
            id?: string;
            name?: string;
            definition?: string;
            domain?: Domain;
            primaryNamespace?: string;
            importedOntologies?: { iri: string; label: string }[];
            localOntologies?: { iri: string; label: string }[];
        }
    ) {
        this.options = options;
        this.schemaFactory = schemaFactory;

        // Domain is required - establishes existential dependency
        if (!contextConfig?.domain) {
            throw new Error('OntologyEnabledBoundedContext requires a domain to be specified in contextConfig');
        }

        // Establish rigid/existential domain dependency first
        this._domain = contextConfig.domain;
        this.domainId = contextConfig.domain.id;

        // Initialize BoundedContext identity
        const counter = ++OntologyEnabledBoundedContext.ontologyEnabledContextCounter;
        this.localId = contextConfig?.id ?? `context-${counter}`;
        this.id = this.localId; // Surrogate ID equals local ID for backward compatibility
        this.uri = `urn:semantical:context:${this.id}`;
        this.name = contextConfig?.name ?? `Bounded Context ${counter}`;
        this.definition = contextConfig?.definition;

        // Register with domain
        this._domain.addBoundedContext(this);

        // Initialize contextual model
        const primaryNs = contextConfig?.primaryNamespace ?? 'http://example.org/default#';
        this._model = new ModelImpl({
            id: `${this.id}-model`,
            name: `${this.name} Model`,
            primaryNamespace: primaryNs,
            importedOntologies: contextConfig?.importedOntologies,
            localOntologies: contextConfig?.localOntologies
        });

        // Initialize Ubiquitous Language
        this._ubiquitousLanguage = new UbiquitousLanguageImpl({
            id: `${this.id}-ul`,
            name: `${this.name} Language`
        });
    }

    /**
     * Get the domain this bounded context belongs to.
     * Implements BoundedContext.getDomain()
     */
    getDomain(): Domain {
        return this._domain;
    }

    /**
     * Get the contextual model for this bounded context.
     * Implements BoundedContext.getModel()
     */
    getModel(): Model {
        return this._model;
    }

    /**
     * Get the Ubiquitous Language for this bounded context.
     * Implements BoundedContext.getUbiquitousLanguage()
     */
    getUbiquitousLanguage(): UbiquitousLanguage {
        return this._ubiquitousLanguage;
    }

    /**
     * Add an imported vocabulary to the Ubiquitous Language.
     */
    addImportedVocabulary(ref: VocabularyRef): void {
        this._ubiquitousLanguage.addImportedVocabulary(ref);
    }

    /**
     * Add a local vocabulary to the Ubiquitous Language.
     */
    addLocalVocabulary(ref: VocabularyRef): void {
        this._ubiquitousLanguage.addLocalVocabulary(ref);
    }

    /**
     * Set the ontology loader for resolving imports.
     * This allows configuring the loader after construction.
     */
    setOntologyLoader(loader: (iri: string) => Promise<OntologySchema | undefined>): void {
        this.options.ontologyLoader = loader;
    }

    /**
     * Set the schema factory for creating OntologySchema from DomainConfig.
     * This allows configuring the factory after construction.
     */
    setSchemaFactory(factory: (domain: DomainConfig) => Promise<OntologySchema>): void {
        this.schemaFactory = factory;
    }

    /**
     * Add an ontology to the bounded context and eagerly load its imports.
     *
     * The ontology is stored by its ontologyIRI. If the ontology has imports
     * (owl:imports), they are recursively loaded using the domain registry
     * or custom ontologyLoader.
     *
     * @param schema - The OntologySchema to add
     * @param sourceUrl - Optional source URL (for duplicate detection when import URL differs from ontology IRI)
     */
    async addOntology(schema: OntologySchema, sourceUrl?: string): Promise<void> {
        const iri = schema.ontologyIRI;

        if (!iri) {
            console.warn(`Ontology "${schema.name}" has no ontologyIRI, storing by name`);
            this.ontologies.set(schema.name, schema);
            return;
        }

        // Check for duplicates by ontology IRI
        if (this.ontologies.has(iri)) {
            console.debug(`Ontology "${iri}" already loaded, skipping`);
            // Still record the source URL as an alias
            if (sourceUrl) {
                this.sourceUrlToIri.set(sourceUrl, iri);
            }
            return;
        }

        // Check for duplicates by source URL (handles case where same ontology is imported via different URLs)
        if (sourceUrl && this.sourceUrlToIri.has(sourceUrl)) {
            console.debug(`Ontology from "${sourceUrl}" already loaded, skipping`);
            return;
        }

        // Add to collection
        this.ontologies.set(iri, schema);
        console.debug(`BoundedContextImpl.addOntology: Added "${schema.name}" with IRI "${iri}"`);

        // Also add to the contextual model's semantic layer
        // If this is the first ontology, set it as the primary namespace
        const isFirstOntology = this._model.getOntologies().length === 0;
        this._model.addOntology(schema);
        if (isFirstOntology) {
            const ns = schema.getNamespace();
            this._model.setPrimaryNamespace(ns);
            console.debug(`BoundedContextImpl.addOntology: Set primary namespace to "${ns}"`);
        }

        // Record source URL mapping
        if (sourceUrl) {
            this.sourceUrlToIri.set(sourceUrl, iri);
            console.debug(`BoundedContextImpl.addOntology: Mapped sourceUrl "${sourceUrl}" -> "${iri}"`);
        }

        // Invalidate caches
        this.classColorCache = null;

        // Eagerly load imports
        console.debug(`BoundedContextImpl.addOntology: Loading ${schema.imports.length} imports for "${schema.name}"`);
        await this.loadImports(schema);
    }

    /**
     * Get an ontology by its IRI or namespace.
     * Handles IRI normalization (with/without trailing # or /).
     * Also matches by namespace since UI often uses namespace as the identifier.
     *
     * @param iri - The ontology IRI or namespace to look up
     * @returns The OntologySchema if found, undefined otherwise
     */
    getOntologyByIRI(iri: string): OntologySchema | undefined {
        // Try direct lookup first
        const direct = this.ontologies.get(iri);
        if (direct) return direct;

        // Normalize the input IRI (remove trailing # or /)
        const normalizedInput = iri.replace(/[#/]$/, '');

        // Try to find by normalized ontologyIRI or by namespace
        for (const [storedIri, schema] of this.ontologies) {
            const normalizedStored = storedIri.replace(/[#/]$/, '');
            if (normalizedInput === normalizedStored) {
                return schema;
            }

            // Also check if input matches the ontology's namespace
            const schemaNamespace = schema.getNamespace().replace(/[#/]$/, '');
            if (normalizedInput === schemaNamespace) {
                return schema;
            }
        }

        return undefined;
    }

    /**
     * Recursively load all imports for an ontology.
     *
     * Handles cyclic imports gracefully by tracking which IRIs are currently
     * being loaded. When a cycle is detected, the import is skipped.
     *
     * @param schema - The OntologySchema whose imports should be loaded
     */
    async loadImports(schema: OntologySchema): Promise<void> {
        const iri = schema.ontologyIRI;
        const onProgress = this.currentProgressCallback;

        // Add current ontology to loading set to detect cycles
        if (iri) {
            this.loadingIris.add(iri);
        }

        if (schema.imports.length > 0) {
            onProgress?.(`Found ${schema.imports.length} imports for ${schema.name}`);
        }

        try {
            let importIndex = 0;
            for (const importIri of schema.imports) {
                importIndex++;

                // Extract short name from import IRI for display
                const shortName = this.extractShortNameFromIri(importIri);

                // Skip if already loaded (check both ontology IRI and source URL)
                if (this.isOntologyLoaded(importIri)) {
                    console.debug(`Import "${importIri}" already loaded, skipping`);
                    continue;
                }

                // Check for cycle: if this import is already being loaded up the call stack
                if (this.loadingIris.has(importIri)) {
                    console.debug(`Cyclic import detected for "${importIri}", skipping`);
                    continue;
                }

                onProgress?.(`Resolving import ${importIndex}/${schema.imports.length}: ${shortName}...`);

                // Mark as loading to prevent parallel duplicate loads
                this.loadingIris.add(importIri);

                // Try to resolve the import
                const importedSchema = await this.resolveImport(importIri);
                if (importedSchema) {
                    onProgress?.(`Loading import: ${importedSchema.name}...`);
                    // Pass the import IRI as source URL for duplicate detection
                    await this.addOntology(importedSchema, importIri);
                } else if (this.options.strictImports) {
                    throw new Error(`Failed to resolve import: ${importIri}`);
                } else {
                    console.warn(`Could not resolve import: ${importIri}`);
                }

                this.loadingIris.delete(importIri);
            }
        } finally {
            if (iri) {
                this.loadingIris.delete(iri);
            }
        }
    }

    /**
     * Extract a short display name from an IRI.
     */
    private extractShortNameFromIri(iri: string): string {
        // Remove trailing slash or hash
        const cleanIri = iri.replace(/[#/]$/, '');
        // Get the last segment
        const lastSlash = cleanIri.lastIndexOf('/');
        if (lastSlash >= 0) {
            return cleanIri.substring(lastSlash + 1);
        }
        return cleanIri.substring(0, 30) + (cleanIri.length > 30 ? '...' : '');
    }

    /**
     * Check if an ontology is already loaded by IRI or source URL.
     *
     * @param iri - The IRI or URL to check
     * @returns true if the ontology is already loaded
     */
    private isOntologyLoaded(iri: string): boolean {
        // Check by ontology IRI
        if (this.ontologies.has(iri)) {
            console.debug(`BoundedContextImpl.isOntologyLoaded: "${iri}" found by ontologyIRI`);
            return true;
        }
        // Check by source URL
        if (this.sourceUrlToIri.has(iri)) {
            console.debug(`BoundedContextImpl.isOntologyLoaded: "${iri}" found by sourceUrl -> ${this.sourceUrlToIri.get(iri)}`);
            return true;
        }
        // Check if currently being loaded
        if (this.loadingIris.has(iri)) {
            console.debug(`BoundedContextImpl.isOntologyLoaded: "${iri}" currently loading`);
            return true;
        }
        return false;
    }

    /**
     * Resolve an import IRI to an OntologySchema.
     *
     * First tries the custom ontologyLoader if provided, then falls back
     * to the domain registry using the schemaFactory.
     *
     * @param iri - The import IRI to resolve
     * @returns The resolved OntologySchema or undefined
     */
    private async resolveImport(iri: string): Promise<OntologySchema | undefined> {
        // First, try custom loader if provided
        if (this.options.ontologyLoader) {
            const result = await this.options.ontologyLoader(iri);
            if (result) return result;
        }

        // Then, try domain registry with schemaFactory
        if (this.schemaFactory) {
            const domain = getDomainByNamespace(iri);
            if (domain) {
                return this.schemaFactory(domain);
            }
        }

        return undefined;
    }

    /**
     * Get all loaded ontologies.
     *
     * @returns Array of all OntologySchema instances in the bounded context
     */
    getAllOntologies(): OntologySchema[] {
        return Array.from(this.ontologies.values());
    }

    /**
     * Remove an ontology from the bounded context.
     *
     * Note: This does not remove ontologies that were imported by this one,
     * nor does it remove this ontology from the imports of other ontologies.
     *
     * @param iri - The ontology IRI to remove
     * @returns true if found and removed, false if not found
     */
    removeOntology(iri: string): boolean {
        return this.ontologies.delete(iri);
    }

    /**
     * Check if an ontology is loaded in the bounded context.
     * Checks both by ontology IRI and by source URL.
     *
     * @param iri - The ontology IRI or source URL to check
     * @returns true if the ontology is loaded
     */
    hasOntology(iri: string): boolean {
        return this.ontologies.has(iri) || this.sourceUrlToIri.has(iri);
    }

    /**
     * Load an ontology from a remote URL and add it to the bounded context.
     *
     * Supports Turtle, RDF/XML, and OWL2/XML formats with auto-detection.
     * After loading the primary ontology, its imports (owl:imports) are
     * recursively resolved using the standard import resolution mechanism.
     *
     * @param url - The URL to fetch the ontology from
     * @returns LoadResult indicating success/failure
     *
     * @example
     * ```typescript
     * const result = await boundedContext.loadFromUrl('https://example.org/ontology.rdf');
     * if (result.success) {
     *   console.log(`Loaded ontology: ${result.ontology?.ontologyIRI}`);
     * } else {
     *   console.error(`Failed to load: ${result.error}`);
     * }
     * ```
     */
    async loadFromUrl(url: string, onProgress?: ProgressCallback): Promise<import('@semantical/services/ontology').LoadResult> {
        // Check if already loaded or currently loading
        if (this.isOntologyLoaded(url)) {
            console.debug(`BoundedContextImpl.loadFromUrl: "${url}" already loaded, skipping`);
            return {
                success: true,
                sourceUrl: url,
                ontology: undefined // Already loaded
            };
        }

        // Mark as loading to prevent parallel duplicate loads
        this.loadingIris.add(url);

        // Store progress callback for use during import resolution
        this.currentProgressCallback = onProgress;

        try {
            // Dynamically import to avoid circular dependency
            const { remoteOntologyLoader } = await import('@semantical/services/ontology');
            const { createOntologySchemaFromOntology } = await import('./OntologySchemaFactory.ts');

            const result = await remoteOntologyLoader.load(url, onProgress);

            if (!result.success || !result.ontology) {
                return result;
            }

            // Create OntologySchema from the parsed Ontology
            const schema = createOntologySchemaFromOntology(result.ontology, url);
            onProgress?.(`Processing ${schema.name}...`);

            // Add to bounded context (this handles import resolution)
            // Pass URL as sourceUrl for duplicate detection
            await this.addOntology(schema, url);

            onProgress?.(`Loaded ${this.size} ontologies`);

            return result;
        } finally {
            this.loadingIris.delete(url);
            this.currentProgressCallback = undefined;
        }
    }

    /**
     * Get the number of ontologies loaded in the bounded context.
     */
    get size(): number {
        return this.ontologies.size;
    }

    /**
     * Clear all ontologies from the bounded context.
     */
    clear(): void {
        this.ontologies.clear();
        this.sourceUrlToIri.clear();
        this.loadingIris.clear();
        this.classColorCache = null;
        // Also clear the contextual model's semantic layer
        this._model.clear();
    }

    /**
     * Get aggregated prefix map from all loaded ontologies.
     *
     * Combines prefix declarations from all ontologies in the bounded context.
     * If multiple ontologies declare the same prefix, the first one wins.
     * Returns a map from namespace IRI to prefix name.
     *
     * @returns Map<namespace, prefix> from all loaded ontologies
     */
    getPrefixes(): Map<string, string> {
        const prefixes = new Map<string, string>();

        for (const schema of this.ontologies.values()) {
            for (const [namespace, prefix] of schema.prefixes) {
                // Only add if namespace not already mapped and prefix is not empty
                // Empty prefix (default namespace) should never be exported from bounded context
                if (!prefixes.has(namespace) && prefix !== '') {
                    prefixes.set(namespace, prefix);
                }
            }
        }

        return prefixes;
    }

    /**
     * Get all namespaces from loaded ontologies.
     *
     * Returns an array of all namespace URIs from the loaded ontologies.
     *
     * @returns Array of namespace URIs
     */
    getNamespaces(): string[] {
        const namespaces: string[] = [];
        for (const schema of this.ontologies.values()) {
            namespaces.push(schema.getNamespace());
        }
        return namespaces;
    }

    /**
     * Get the label for an ontology identified by namespace.
     *
     * Searches for the ontology by normalizing the namespace (removing trailing #)
     * and checking both the namespace and ontologyIRI. Extracts the ontology's
     * label annotation with language preference (defaults to English).
     *
     * Checks the following annotation properties in order:
     * - rdfs:label
     * - dcterms:title
     * - dct:title
     *
     * @param namespace - The namespace IRI to look up (with or without trailing #)
     * @param preferredLanguage - The preferred language tag (default: 'en')
     * @returns The ontology label in the preferred language, or undefined if not found
     */
    getOntologyLabel(namespace: string, preferredLanguage: string = 'en'): string | undefined {
        // Normalize namespace by removing trailing #
        const normalizeNs = (ns: string) => ns.replace(/#$/, '');
        const normalizedNamespace = normalizeNs(namespace);

        // Try to find the ontology by namespace or ontologyIRI
        let ontologySchema: OntologySchema | undefined;

        for (const schema of this.ontologies.values()) {
            const schemaNamespace = normalizeNs(schema.getNamespace());
            const schemaOntologyIRI = normalizeNs(schema.ontologyIRI);

            if (schemaNamespace === normalizedNamespace || schemaOntologyIRI === normalizedNamespace) {
                ontologySchema = schema;
                break;
            }
        }

        if (!ontologySchema?.ontology) {
            return undefined;
        }

        const ontologyIRI = ontologySchema.ontologyIRI;
        const onto = ontologySchema.ontology;

        // Try multiple annotation properties in order of preference
        const annotationProperties = [
            'http://www.w3.org/2000/01/rdf-schema#label',      // rdfs:label
            'http://purl.org/dc/terms/title',                   // dcterms:title
            'http://purl.org/dc/elements/1.1/title',           // dct:title
        ];

        for (const propertyIRI of annotationProperties) {
            const label = getAnnotationValueWithLanguagePreference(
                onto,
                ontologyIRI,
                propertyIRI,
                preferredLanguage
            );
            if (label) {
                return label;
            }
        }

        return undefined;
    }

    /**
     * Build aggregated class taxonomy from all loaded ontologies.
     *
     * Combines class declarations from all ontologies in the bounded context.
     * Only includes classes from the allowed namespaces.
     *
     * IMPORTANT: This method now uses buildClassTaxonomyFromMultipleOntologies
     * to properly handle cross-ontology inheritance (e.g., dprod:DataProduct
     * subClassOf dcat:Resource). Previously, each ontology was processed
     * separately which caused child classes to be lost when their parent was
     * in a different ontology.
     *
     * @param primaryNamespace - The primary namespace (used for isImported flag)
     * @param allowedNamespaces - Namespaces to include (defaults to all loaded ontology namespaces)
     * @returns Array of ClassTaxonomyNode trees
     */
    getClassTaxonomy(
        primaryNamespace: string,
        allowedNamespaces?: string[]
    ): ClassTaxonomyNode[] {
        const namespaces = allowedNamespaces || this.getNamespaces();
        const prefixes = this.getPrefixes();

        // Collect all ontologies
        const ontologies = Array.from(this.ontologies.values())
            .map(schema => schema.ontology)
            .filter((onto): onto is NonNullable<typeof onto> => onto !== undefined);

        // Build unified taxonomy from all ontologies at once
        // This ensures cross-ontology parent-child relationships work correctly
        return buildClassTaxonomyFromMultipleOntologies(
            ontologies,
            primaryNamespace,
            namespaces,
            prefixes
        );
    }

    /**
     * Build aggregated relationship taxonomy from all loaded ontologies.
     *
     * Combines object property declarations from all ontologies in the bounded context.
     * Only includes properties from the allowed namespaces.
     *
     * @param primaryNamespace - The primary namespace
     * @param allowedNamespaces - Namespaces to include (defaults to all loaded ontology namespaces)
     * @returns Array of RelationshipTaxonomyNode trees
     */
    getRelationshipTaxonomy(
        primaryNamespace: string,
        allowedNamespaces?: string[]
    ): RelationshipTaxonomyNode[] {
        const namespaces = allowedNamespaces || this.getNamespaces();
        const allNodes: RelationshipTaxonomyNode[] = [];

        for (const schema of this.ontologies.values()) {
            if (!schema.ontology) continue;

            const nodes = buildRelationshipTaxonomyFromOntology(
                schema.ontology,
                primaryNamespace,
                namespaces
            );
            allNodes.push(...nodes);
        }

        // Remove duplicates based on URI (use id as fallback)
        const seen = new Set<string>();
        return allNodes.filter(node => {
            const key = node.uri || node.id;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
    }

    /**
     * Build aggregated class hierarchy from all loaded ontologies.
     *
     * Combines subclass relationships from all ontologies in the bounded context.
     *
     * @returns Map from child class URI to array of parent class URIs
     */
    getClassHierarchy(): ClassHierarchyMap {
        const combined = new Map<string, string[]>();

        for (const schema of this.ontologies.values()) {
            if (!schema.ontology) continue;

            const hierarchy = buildClassHierarchyFromOntology(schema.ontology);

            for (const [child, parents] of hierarchy) {
                const existing = combined.get(child) || [];
                for (const parent of parents) {
                    if (!existing.includes(parent)) {
                        existing.push(parent);
                    }
                }
                combined.set(child, existing);
            }
        }

        return combined;
    }

    /**
     * Get all known object property URIs from loaded ontology schemas.
     * Used by the parser to recognize object property assertions from external ontologies.
     *
     * @returns Set of object property URIs
     */
    getObjectPropertyUris(): Set<string> {
        const uris = new Set<string>();
        for (const schema of this.ontologies.values()) {
            if (!schema.ontology) continue;
            for (const axiom of schema.ontology.axioms) {
                if ('entity' in axiom &&
                    axiom.entity.entityType === 'ObjectProperty' &&
                    axiom.entity.iri.kind === 'FullIRI') {
                    uris.add(axiom.entity.iri.value);
                }
            }
        }
        return uris;
    }

    /**
     * Get all known data property URIs from loaded ontology schemas.
     * Used by the parser to recognize data property assertions from external ontologies.
     *
     * @returns Set of data property URIs
     */
    getDataPropertyUris(): Set<string> {
        const uris = new Set<string>();
        for (const schema of this.ontologies.values()) {
            if (!schema.ontology) continue;
            for (const axiom of schema.ontology.axioms) {
                if ('entity' in axiom &&
                    axiom.entity.entityType === 'DataProperty' &&
                    axiom.entity.iri.kind === 'FullIRI') {
                    uris.add(axiom.entity.iri.value);
                }
            }
        }
        return uris;
    }

    /**
     * Get individuals by class URI from all loaded ontologies (TBox).
     * Searches all ontologies and returns individuals that are instances of the given class
     * or any of its subclasses.
     *
     * @param classUri - The class URI to search for
     * @returns Array of Individual objects
     */
    getIndividualsByClass(classUri: string): import("@semantical/modules/semantical-core").Individual[] {
        const result: import("@semantical/modules/semantical-core").Individual[] = [];
        const seenUris = new Set<string>();

        for (const schema of this.ontologies.values()) {
            const individuals = schema.getIndividualsByClass(classUri);
            for (const ind of individuals) {
                if (!seenUris.has(ind.uri)) {
                    seenUris.add(ind.uri);
                    result.push(ind);
                }
            }
        }
        return result;
    }

    /**
     * Get an individual by URI from all sources (KnowledgeGraphs and ontologies).
     * Searches KnowledgeGraphs (ABox) first, then ontologies (TBox).
     *
     * @param uri - The individual URI to search for
     * @returns The Individual or undefined if not found
     */
    getIndividualByUri(uri: string): import("@semantical/modules/semantical-core").Individual | undefined {
        // Search KnowledgeGraphs (ABox) first
        for (const kg of this.knowledgeGraphs.values()) {
            const individual = kg.getIndividualByUri(uri);
            if (individual) return individual;
        }
        // Then search ontologies (TBox)
        for (const schema of this.ontologies.values()) {
            const individual = schema.getIndividualByUri(uri);
            if (individual) return individual;
        }
        return undefined;
    }

    /**
     * Get the ontology that contains a specific individual (TBox).
     * Useful for determining sourceGraphIRI when adding SchemaIndividuals to diagrams.
     *
     * @param uri - The individual URI to search for
     * @returns The OntologySchema containing the individual, or undefined if not found
     */
    getOntologyForIndividual(uri: string): OntologySchema | undefined {
        for (const schema of this.ontologies.values()) {
            if (schema.getIndividualByUri(uri)) {
                return schema;
            }
        }
        return undefined;
    }

    /**
     * Get all SchemaIndividuals from all loaded ontologies (TBox).
     *
     * @returns Array of all SchemaIndividuals from all ontologies
     */
    getAllSchemaIndividuals(): import("@semantical/modules/ontology-model").SchemaIndividual[] {
        const result: import("@semantical/modules/ontology-model").SchemaIndividual[] = [];
        for (const schema of this.ontologies.values()) {
            for (const individual of schema.individuals.values()) {
                result.push(individual);
            }
        }
        return result;
    }

    /**
     * Get the inferred KnowledgeGraph.
     * Returns null if no inference has been run yet.
     *
     * @returns The inferred KnowledgeGraph or null
     */
    getInferredKnowledgeGraph(): KnowledgeGraph | null {
        return this.inferredKnowledgeGraph;
    }

    /**
     * Set the inferred KnowledgeGraph.
     * Called by the InferenceEngine after running inference.
     *
     * @param kg - The KnowledgeGraph containing inferred relationships
     */
    setInferredKnowledgeGraph(kg: KnowledgeGraph | null): void {
        this.inferredKnowledgeGraph = kg;
    }

    /**
     * Clear the inferred KnowledgeGraph.
     * Call this when inference is disabled or when the model changes.
     */
    clearInferredKnowledgeGraph(): void {
        this.inferredKnowledgeGraph = null;
    }

    /** Cached class color map (built lazily from taxonomy) */
    private classColorCache: Map<string, string> | null = null;

    /** Map of namespace IRI to color for visualization */
    private namespaceColors: Map<string, string> = new Map();

    /**
     * Get the category color for a class by its full URI.
     * Searches class taxonomy built from all loaded ontologies.
     *
     * @param classUri - The full URI of the class
     * @returns The category color string or undefined if not found
     */
    getClassColor(classUri: string): string | undefined {
        // Build cache if not already built
        if (!this.classColorCache) {
            this.classColorCache = new Map();
            const namespaces = this.getNamespaces();
            const primary = namespaces.length > 0 ? namespaces[0] : '';

            for (const schema of this.ontologies.values()) {
                if (!schema.ontology) continue;

                const nodes = buildClassTaxonomyFromOntology(
                    schema.ontology,
                    primary,
                    namespaces
                );

                // Traverse and cache all colors
                const cacheColors = (nodeList: ClassTaxonomyNode[]) => {
                    for (const node of nodeList) {
                        if (node.uri && node.categoryColor) {
                            this.classColorCache!.set(node.uri, node.categoryColor);
                        }
                        if (node.children) {
                            cacheColors(node.children);
                        }
                    }
                };
                cacheColors(nodes);
            }
        }

        return this.classColorCache.get(classUri);
    }

    /**
     * Clear the class color cache (call when ontologies change)
     */
    clearClassColorCache(): void {
        this.classColorCache = null;
    }

    /**
     * Get the label of a class by its URI.
     * Searches all loaded ontologies.
     *
     * @param classUri - The full URI of the class
     * @returns The class label or undefined if not found
     */
    getClassLabel(classUri: string): string | undefined {
        for (const schema of this.ontologies.values()) {
            const schemaClass = schema.getClassByUri(classUri);
            if (schemaClass) {
                return schemaClass.label || schemaClass.name;
            }
        }
        return undefined;
    }

    /**
     * Set a color for a specific namespace.
     *
     * @param namespace - The namespace IRI
     * @param color - The hex color (e.g., "#3b82f6")
     */
    setNamespaceColor(namespace: string, color: string): void {
        this.namespaceColors.set(namespace, color);
        this.classColorCache = null; // Invalidate cache
    }

    /**
     * Get the color for a namespace.
     *
     * @param namespace - The namespace IRI
     * @returns The hex color or undefined if not set
     */
    getNamespaceColor(namespace: string): string | undefined {
        return this.namespaceColors.get(namespace);
    }

    /**
     * Get all namespace colors.
     *
     * @returns A copy of the namespace colors map
     */
    getAllNamespaceColors(): Map<string, string> {
        return new Map(this.namespaceColors);
    }

    /**
     * Clear the color for a specific namespace.
     *
     * @param namespace - The namespace IRI
     */
    clearNamespaceColor(namespace: string): void {
        this.namespaceColors.delete(namespace);
        this.classColorCache = null; // Invalidate cache
    }

    /**
     * Get namespace tag info (prefix and color) for a given source graph IRI.
     * Used by DiagramClassElement to display consistent namespace badges.
     *
     * @param sourceGraphIRI - The source graph/ontology IRI
     * @returns Object with prefix (if found) and color (if set), or undefined if no match
     */
    getNamespaceTag(sourceGraphIRI: string): { prefix?: string; color?: string } | undefined {
        const prefixes = this.getPrefixes();

        // First, try to find the ontology by its IRI and use its namespace
        const ontology = this.getOntologyByIRI(sourceGraphIRI);
        const ontologyNamespace = ontology?.getNamespace() || sourceGraphIRI;

        // Helper to find prefix and actual namespace URI for an ontology
        // Handles cases where ontology IRI differs from term namespace (e.g., dcat vs dcat#)
        const findPrefixAndNamespace = (namespace: string): { prefix?: string; namespace: string } => {
            // Try exact match first
            let prefix = prefixes.get(namespace);
            if (prefix) {
                return { prefix, namespace };
            }

            // Try with # appended
            const withHash = namespace.endsWith('#') ? namespace : namespace + '#';
            prefix = prefixes.get(withHash);
            if (prefix) {
                return { prefix, namespace: withHash };
            }

            // Try with / appended
            const withSlash = namespace.endsWith('/') ? namespace : namespace + '/';
            prefix = prefixes.get(withSlash);
            if (prefix) {
                return { prefix, namespace: withSlash };
            }

            // Try to find a prefix where the namespace starts with the ontology namespace
            for (const [ns, pre] of prefixes.entries()) {
                if (ns.startsWith(namespace)) {
                    return { prefix: pre, namespace: ns };
                }
            }

            // No prefix found
            return { namespace };
        };

        const result = findPrefixAndNamespace(ontologyNamespace);

        // Get color for the namespace (if set)
        const color = this.getNamespaceColor(result.namespace);

        return {
            prefix: result.prefix,
            color: color
        };
    }

    // ==================== View Interface (BoundedContext) ====================

    /**
     * Get all views owned by this bounded context.
     * The array order is authoritative - views are displayed in this order.
     * Implements BoundedContext.getViews()
     */
    getViews(): View[] {
        return this._views;
    }

    /**
     * Get all datasets owned by this bounded context.
     * The array order is authoritative - datasets are displayed in this order.
     * Implements BoundedContext.getDatasets()
     */
    getDatasets(): import("@semantical/modules/semantical-core").DatasetRef[] {
        return this._datasets;
    }

    // ==================== Dataset-Specific Methods ====================

    /**
     * Get a KnowledgeGraph by dataset ID.
     */
    getKnowledgeGraphByDatasetId(datasetId: string): KnowledgeGraph | undefined {
        return this.knowledgeGraphs.get(datasetId);
    }

    /**
     * Add or update a dataset reference without a KnowledgeGraph.
     * Use this when loading from config - the KG will be loaded lazily via persistence service.
     * New datasets are appended to the end.
     */
    setDatasetRef(datasetRef: import("@semantical/modules/semantical-core").DatasetRef): void {
        console.debug(`OntologyEnabledBoundedContext.setDatasetRef: BC ${this.id} adding dataset ${datasetRef.name} (${datasetRef.id})`);
        const existingIndex = this._datasets.findIndex(d => d.id === datasetRef.id);
        if (existingIndex >= 0) {
            // Replace in same position to preserve order
            this._datasets[existingIndex] = datasetRef;
            console.debug(`OntologyEnabledBoundedContext.setDatasetRef: Replaced at index ${existingIndex}, total: ${this._datasets.length}`);
        } else {
            // Append new dataset
            this._datasets.push(datasetRef);
            console.debug(`OntologyEnabledBoundedContext.setDatasetRef: Appended, total: ${this._datasets.length}`);
        }
    }

    /**
     * Add or update a dataset with its associated KnowledgeGraph.
     * New datasets are appended to the end.
     */
    setDataset(datasetRef: import("@semantical/modules/semantical-core").DatasetRef, knowledgeGraph: KnowledgeGraph): void {
        const existingIndex = this._datasets.findIndex(d => d.id === datasetRef.id);
        if (existingIndex >= 0) {
            // Replace in same position to preserve order
            this._datasets[existingIndex] = datasetRef;
        } else {
            // Append new dataset
            this._datasets.push(datasetRef);
        }
        this.knowledgeGraphs.set(datasetRef.id, knowledgeGraph);
    }

    // ==================== View-Specific Methods ====================

    /**
     * Get a view by ID.
     */
    getView(viewId: string): View | undefined {
        return this._views.find(v => v.id === viewId);
    }

    /**
     * Register a view in the bounded context.
     * If a view with the same ID exists, it is replaced in the same position.
     * New views are appended to the end.
     *
     * This method does NOT create or register a dataset/KnowledgeGraph.
     * Callers are responsible for linking views to datasets as needed.
     *
     * @param view - The View instance to register
     */
    registerView(view: View): void {
        const existingIndex = this._views.findIndex(v => v.id === view.id);
        if (existingIndex >= 0) {
            // Replace in same position to preserve order
            this._views[existingIndex] = view;
        } else {
            // Append new view
            this._views.push(view);
        }
    }

    /**
     * Get the KnowledgeGraph for a specific view by ID.
     * Note: The view must have been linked to a dataset for this to return a result.
     */
    getKnowledgeGraph(viewId: string): KnowledgeGraph | undefined {
        return this.knowledgeGraphs.get(viewId);
    }

    /**
     * Check if a view with the given ID exists.
     */
    hasView(viewId: string): boolean {
        return this._views.some(v => v.id === viewId);
    }

    /**
     * Remove a view and its associated KnowledgeGraph (if any).
     */
    removeView(viewId: string): boolean {
        const index = this._views.findIndex(v => v.id === viewId);
        if (index >= 0) {
            this._views.splice(index, 1);
            this.knowledgeGraphs.delete(viewId);
            return true;
        }
        return false;
    }

    /**
     * Clear all views, datasets, and their associated KnowledgeGraphs.
     */
    clearViews(): void {
        this._views.length = 0;
        this._datasets.length = 0;
        this.knowledgeGraphs.clear();
    }

    /**
     * Get all view IDs in order.
     */
    getAllViewIds(): string[] {
        return this._views.map(v => v.id);
    }

    /**
     * Move a view to a new position in the list.
     * @param viewId - The ID of the view to move
     * @param newIndex - The target index (0-based)
     * @returns true if the view was moved, false if not found
     */
    moveView(viewId: string, newIndex: number): boolean {
        const currentIndex = this._views.findIndex(v => v.id === viewId);
        if (currentIndex < 0) return false;

        const [view] = this._views.splice(currentIndex, 1);
        const targetIndex = Math.max(0, Math.min(newIndex, this._views.length));
        this._views.splice(targetIndex, 0, view);
        return true;
    }

    /**
     * Serialize to JSON-LD with per-object @context.
     */
    toJSON(options?: { includeDiagrams?: boolean; includeDatasets?: boolean }): {
        '@context': Record<string, unknown>;
        '@type': 'BoundedContext';
        '@id': string;
        name: string;
        definition?: string;
        hasModel: ReturnType<ModelImpl['toJSON']>;
        hasUbiquitousLanguage?: ReturnType<UbiquitousLanguageImpl['toJSON']>;
        diagrams?: unknown[];
        datasets?: unknown[];
    } {
        // Build @context with set container aliases for arrays
        const context: Record<string, unknown> = {
            '@vocab': 'http://semantical.io/ontology/core#'
        };

        if (options?.includeDiagrams) {
            context.diagrams = { '@id': 'hasDiagram', '@type': '@id', '@container': '@set' };
        }
        if (options?.includeDatasets) {
            context.datasets = { '@id': 'hasDataset', '@type': '@id', '@container': '@set' };
        }

        const result: {
            '@context': Record<string, unknown>;
            '@type': 'BoundedContext';
            '@id': string;
            name: string;
            definition?: string;
            hasModel: ReturnType<ModelImpl['toJSON']>;
            hasUbiquitousLanguage?: ReturnType<UbiquitousLanguageImpl['toJSON']>;
            diagrams?: unknown[];
            datasets?: unknown[];
        } = {
            '@context': context,
            '@type': 'BoundedContext',
            '@id': this.uri,
            name: this.name,
            hasModel: this._model.toJSON()
        };

        if (this.definition) {
            result.definition = this.definition;
        }

        // Only include UbiquitousLanguage if it has vocabularies
        const allVocabularies = this._ubiquitousLanguage.getAllVocabularies();
        if (allVocabularies.length > 0) {
            result.hasUbiquitousLanguage = this._ubiquitousLanguage.toJSON();
        }

        // Include views (diagrams) if requested
        if (options?.includeDiagrams && this._views.length > 0) {
            result.diagrams = this._views.map(v => (v as any).toJSON?.() ?? v);
        }

        // Include datasets if requested
        if (options?.includeDatasets && this._datasets.length > 0) {
            result.datasets = this._datasets.map(ds => ({
                '@type': 'Dataset',
                '@id': `urn:semantical:dataset:${ds.id}`,
                name: ds.name,
                datasetType: ds.type
            }));
        }

        return result;
    }
}

export default OntologyEnabledBoundedContext;
